import { Axiom } from '@axiomhq/js'
import { shallowReactive, toRaw } from 'vue'
import type { Dayjs } from 'dayjs'
import { mergeVersionMetrics, versionMetricsApl } from '~/utils/versionMetrics'
import type { VersionMetrics } from '~/utils/versionMetrics'
import { toAplString } from '~/utils/apl'
import { AUDIT_REQUESTS_LIMIT, AUDIT_SIBLINGS_LIMIT, appStoreAuditApl, appStoreAuditRequestsApl, appStoreAuditSiblingsApl, auditEventsFromMatches, auditFiltersNarrow, mergeAuditEvents, unfetchedRequests } from '~/utils/appstoreAudit'
import type { AppStoreAuditEvent, AuditFilters } from '~/utils/appstoreAudit'
const dayjs = useDayjs()


type DateInput = string | number | Date | Dayjs | null | undefined

const toAxiomISOString = (value?: DateInput, fallback?: DateInput) => {
  const fallbackValue = fallback === undefined ? dayjs() : fallback
  const date = value === undefined || value === null || value === '' ? dayjs(fallbackValue) : dayjs(value)
  if (date.isValid()) {
    return date.toISOString()
  }
  const fallbackDate = dayjs(fallbackValue)
  return fallbackDate.isValid() ? fallbackDate.toISOString() : new Date().toISOString()
}

const hasDateInput = (value: DateInput) => value !== undefined && value !== null && value !== ''

const requireAxiomISOString = (value: DateInput, label: string) => {
  if (!hasDateInput(value)) throw new Error(`${label} is required`)
  const date = dayjs(value)
  if (!date.isValid()) throw new Error(`${label} is invalid`)
  return date.toISOString()
}

const requireAxiomTimeRange = (startTime: DateInput, endTime: DateInput) => {
  const queryStartTime = requireAxiomISOString(startTime, 'startTime')
  const queryEndTime = requireAxiomISOString(endTime, 'endTime')
  if (!dayjs(queryEndTime).isAfter(dayjs(queryStartTime))) {
    throw new Error('endTime must be after startTime')
  }
  return { startTime: queryStartTime, endTime: queryEndTime }
}

const toEpochMs = (value: any) => {
  if (!value) return 0
  if (typeof value === 'number') {
    if (value > 1_000_000_000_000_000) return Math.floor(value / 1_000_000)
    if (value > 1_000_000_000_000) return value
    return value * 1000
  }
  const date = dayjs(value)
  return date.isValid() ? date.valueOf() : 0
}

const toStringArray = (value: any) => {
  if (Array.isArray(value)) {
    return value.map(item => String(item || '').trim()).filter(Boolean)
  }
  const text = String(value || '').trim()
  return text ? [text] : []
}

// allServers mirrors the real-time speed page total: skip do_not_count servers and the home group.
const realTimeSpeedServerFilter = (serverList: string[], allServers: boolean) => {
  if (allServers) {
    return `| where tobool(['do_not_count']) != true and ['server'] != "home" and not(['server'] startswith "home-")`
  }
  if (serverList.length === 1) {
    return `| where ['server'] == ${toAplString(serverList[0])}`
  }
  return `| where ['server'] in (${serverList.map(toAplString).join(', ')})`
}

const serverUsageBaseApl = (server?: string) => {
  const serverFilter = server ? `\n        | where serverName == ${toAplString(server)}` : ''
  return `['pandafanproxyservers']
        | extend bytes = todouble(['size-in-byte'])
        | extend serverName = tostring(server)
        | extend protocolName = tostring(protocol)
        | extend userId = tostring(['user-id'])
        | extend userIp = tostring(['user-address'])
        | extend transportName = tostring(['transport'])
        | extend sudokuProto = tostring(['sudoku-proto'])
        | extend protocolDetail = iff(protocolName == "naive" and isnotempty(transportName), strcat(protocolName, " / ", transportName), iff(protocolName == "sudoku" and isnotempty(sudokuProto), strcat(protocolName, " / ", sudokuProto), protocolName))
        | where isnotempty(serverName) and isnotempty(protocolName) and isnotempty(userId) and isnotnull(bytes) and bytes > 0${serverFilter}`
}

declare global {
  type AndroidVersionMetrics = {
    version: string
    installCount: number
    activeUsers: number
    heartbeatCount: number
    launchCount: number
    usageEventCount: number
    trafficBytes: number
    trafficUpBytes: number
    trafficDownBytes: number
    eventDeviceCount: number
    eventUserCount: number
    lastHeartbeatAt: number
    lastEventAt: number
    lastSeenAt: number
  }

  type AppleMetricPlatformName = 'ios' | 'ipados' | 'tvos' | 'macos' | 'visionos' | 'apple'

  type AppleVersionMetrics = {
    version: string
    platform: AppleMetricPlatformName
    installCount: number
    activeUsers: number
    heartbeatCount: number
    launchCount: number
    usageEventCount: number
    trafficBytes: number
    trafficUpBytes: number
    trafficDownBytes: number
    eventDeviceCount: number
    eventUserCount: number
    lastHeartbeatAt: number
    lastEventAt: number
    lastSeenAt: number
  }

  type DesktopVersionMetrics = {
    version: string
    platform: 'darwin' | 'win32'
    installCount: number
    activeUsers: number
    heartbeatCount: number
    launchCount: number
    usageEventCount: number
    trafficBytes: number
    trafficUpBytes: number
    trafficDownBytes: number
    eventDeviceCount: number
    eventUserCount: number
    lastHeartbeatAt: number
    lastEventAt: number
    lastSeenAt: number
  }

  // Per-user breakdown behind one row of DesktopVersionMetrics.
  type DesktopVersionUserMetrics = {
    userId: string
    deviceCount: number
    osVersions: string[]
    heartbeatCount: number
    launchCount: number
    usageEventCount: number
    trafficBytes: number
    trafficUpBytes: number
    trafficDownBytes: number
    lastHeartbeatAt: number
    lastEventAt: number
    lastSeenAt: number
  }

  type AxiomServerUsageSummary = {
    totalBytes: number
    userCount: number
    ipCount: number
    recordCount: number
    serverCount: number
    protocolCount: number
    firstSeenAt: number
    lastSeenAt: number
  }

  type AxiomServerUsageByServer = {
    server: string
    totalBytes: number
    userCount: number
    ipCount: number
    recordCount: number
    protocolCount: number
    protocols: string[]
    firstSeenAt: number
    lastSeenAt: number
  }

  type AxiomServerUsageByProtocol = {
    protocol: string
    protocolDetail: string
    totalBytes: number
    userCount: number
    serverCount: number
    recordCount: number
    firstSeenAt: number
    lastSeenAt: number
  }

  type AxiomServerUsageByServerProtocol = {
    server: string
    protocol: string
    protocolDetail: string
    totalBytes: number
    userCount: number
    ipCount: number
    recordCount: number
    firstSeenAt: number
    lastSeenAt: number
  }

  type AxiomServerUsageByUser = {
    userId: string
    totalBytes: number
    serverCount: number
    protocolCount: number
    ipCount: number
    recordCount: number
    servers: string[]
    protocols: string[]
    firstSeenAt: number
    lastSeenAt: number
  }

  type AxiomServerUsageByServerUser = {
    server: string
    userId: string
    totalBytes: number
    protocolCount: number
    ipCount: number
    recordCount: number
    protocols: string[]
    firstSeenAt: number
    lastSeenAt: number
  }

  type AxiomServerUsageServerDetails = {
    server: string
    protocols: AxiomServerUsageByServerProtocol[]
    users: AxiomServerUsageByServerUser[]
    startTime: string
    endTime: string
  }

  type AxiomServerUsageDashboard = {
    summary: AxiomServerUsageSummary
    servers: AxiomServerUsageByServer[]
    protocols: AxiomServerUsageByProtocol[]
    serverProtocols: AxiomServerUsageByServerProtocol[]
    users: AxiomServerUsageByUser[]
    startTime: string
    endTime: string
  }

  type PandaEnvoyHealthStatus = 'ok' | 'warning' | 'critical'

  type PandaEnvoyUpstreamHost = {
    cluster?: string
    address?: string
    hostname?: string
    health_flags?: string
    healthy?: boolean
    weight?: number
    priority?: number
    cx_active?: number
    cx_connect_fail?: number
    cx_total?: number
    rq_active?: number
    rq_error?: number
    rq_success?: number
    rq_timeout?: number
    rq_total?: number
    success_rate?: number
    local_origin_success_rate?: number
    access_log_lines?: number
    access_log_session_count?: number
    access_log_client_count?: number
    access_log_rx_bytes_delta?: number
    access_log_rx_bytes_per_sec?: number | null
    access_log_tx_bytes_delta?: number
    access_log_tx_bytes_per_sec?: number | null
    access_log_duration_ms_max?: number
    access_log_last_seen?: string
  }

  type PandaEnvoyEvent = {
    _time: Date | string
    kind: 'host' | 'envoy_instance'
    host: string
    health_status?: PandaEnvoyHealthStatus
    health_reasons?: string[]
    instance?: string
    listen_port?: number
    ready?: boolean
    pm2_status?: string
    pid?: number
    fd_count?: number
    fd_limit?: number
    fd_usage_pct?: number
    udp_sockets_visible?: number
    listen_sockets?: number
    downstream_sess_active?: number
    downstream_sess_total_delta?: number | null
    downstream_sess_total_per_sec?: number | null
    rx_mbps?: number | null
    tx_mbps?: number | null
    client_kbps_sample?: number | null
    unique_src_ips_sample?: number | null
    tcpdump_sample_seconds?: number | null
    membership_healthy?: number
    membership_total?: number
    clusters_error?: string
    upstream_hosts?: PandaEnvoyUpstreamHost[] | string
    upstream_host_count?: number
    upstream_healthy_hosts?: number
    default_total_match_count_delta?: number | null
    default_total_match_count_per_sec?: number | null
    udp_sess_rx_datagrams_delta?: number | null
    udp_sess_rx_datagrams_per_sec?: number | null
    udp_sess_tx_datagrams_delta?: number | null
    udp_sess_tx_datagrams_per_sec?: number | null
    udp_sess_rx_datagrams_dropped_delta?: number | null
    udp_sess_rx_errors_delta?: number | null
    udp_sess_tx_errors_delta?: number | null
    udp_sess_tunnel_failure_delta?: number | null
    udp_sess_tunnel_success_delta?: number | null
    udp_sess_tunnel_buffer_overflow_delta?: number | null
    upstream_cx_rx_bytes_total_delta?: number | null
    upstream_cx_rx_bytes_total_per_sec?: number | null
    upstream_cx_tx_bytes_total_delta?: number | null
    upstream_cx_tx_bytes_total_per_sec?: number | null
    access_log_lines?: number
    access_log_session_count?: number
    access_log_client_count?: number
    access_log_rx_bytes_delta?: number
    access_log_rx_bytes_per_sec?: number | null
    access_log_tx_bytes_delta?: number
    access_log_tx_bytes_per_sec?: number | null
    access_log_duration_ms_max?: number
    access_log_last_seen?: string
    upstream_cx_overflow_delta?: number | null
    upstream_cx_connect_fail_delta?: number | null
    upstream_cx_connect_timeout_delta?: number | null
    upstream_cx_none_healthy_delta?: number | null
    downstream_rx_datagram_dropped_delta?: number | null
    downstream_sess_no_route_delta?: number | null
    downstream_sess_rx_errors_delta?: number | null
    downstream_sess_tx_errors_delta?: number | null
    conntrack_count?: number
    conntrack_max?: number
    conntrack_usage_pct?: number
    udp_mem_pressure_pct?: number
    sockstat_udp_inuse?: number
    sockstat_udp_mem?: number
    net_rx_mbps?: number | null
    net_tx_mbps?: number | null
    net_rx_packets_per_sec?: number | null
    net_tx_packets_per_sec?: number | null
    udp_snmp_RcvbufErrors_delta?: number | null
    udp_snmp_SndbufErrors_delta?: number | null
    udp_snmp_MemErrors_delta?: number | null
    udp_snmp_InErrors_delta?: number | null
    udp_snmp_InCsumErrors_delta?: number | null
    [key: string]: any
  }

  type PandaEnvoyDashboard = {
    instances: PandaEnvoyEvent[]
    hostEvents: PandaEnvoyEvent[]
    startTime: string
    endTime: string
  }

  type DesktopClientSummary = {
    ipSet: string[]
    uniqDeviceCount: number
    userNameSet: string[]
    userId: number
  }

  type UsageByIPSeries = {
    startTime: string
    endTime: string
    groups: {
      id: number
      group: {
        userIp: string | null
      }
      aggregations: {
        op: string
        value: number
      }[]
    }[]
  }[]
}

type AxiomRuntime = {
  client: Axiom | null
  axiomConfig: any
  resultCache: Map<string, Promise<any>>
}

// SDK instances, config and pending requests cannot enter Nuxt's SSR payload.
// Keying by store also keeps concurrent SSR requests from sharing a client.
const axiomRuntimes = new WeakMap<object, AxiomRuntime>()
const runtimeFor = (store: object): AxiomRuntime => {
  // Devtools wraps actions in fresh proxies; getters use the original store.
  const owner = toRaw(store)
  let runtime = axiomRuntimes.get(owner)
  if (!runtime) {
    runtime = shallowReactive({
      client: null,
      axiomConfig: null,
      resultCache: new Map<string, Promise<any>>(),
    })
    axiomRuntimes.set(owner, runtime)
  }
  return runtime
}

export const useAxiom = defineStore('axiom', {
  state: () => ({}),
  getters: {
    client(): Axiom | null { return runtimeFor(this).client },
    axiomConfig(): any { return runtimeFor(this).axiomConfig },
    resultCache(): Map<string, Promise<any>> { return runtimeFor(this).resultCache },
  },
  actions: {
    async getClient(createNew: boolean = false): Promise<Axiom> {
      const runtime = runtimeFor(this)
      if (runtime.client && !createNew) return runtime.client

      let configPromise = runtime.resultCache.get('axiomConfig')
      if (!configPromise) {
        configPromise = useAdmin.getAxiomConfig().catch(error => {
          if (runtime.resultCache.get('axiomConfig') === configPromise) runtime.resultCache.delete('axiomConfig')
          throw error
        })
        runtime.resultCache.set('axiomConfig', configPromise)
      }
      runtime.axiomConfig = await configPromise
      // Concurrent initializers can finish the same config request together.
      if (!runtime.client || createNew) runtime.client = new Axiom(runtime.axiomConfig)
      return runtime.client
    },
    async toTable(
      rawData: any,
      opt: {
        extraProcessor: Record<string, (value: any, item?: any) => any>
      } = {
        extraProcessor: {},
      },
    ) {
      if (!opt) {
        opt = {
          extraProcessor: {},
        }
      }
      if (!rawData) {
        return rawData
      }
      // console.log('toTable processing:', rawData)
      const returnArray: any[] = []
      if (rawData.matches?.length > 0) {
        for (const item of rawData.matches) {
          const newItem = { ...item.data, _time: new Date(item._time) }
          for (const key in newItem) {
            if (opt.extraProcessor[key]) {
              newItem[key] = opt.extraProcessor[key](newItem[key], item)
            }
          }
          returnArray.push(newItem)
        }
      } else {
        // console.log('rawData.buckets is', rawData.buckets)
        for (const item of rawData.buckets?.totals || []) {
          const newItem = {
            ...item.group,
          }
          for (const key in item) {
            if (opt?.extraProcessor?.[key]) {
              newItem[key] = await opt?.extraProcessor?.[key](item[key], item)
            }
          }
          for (const ag of item.aggregations) {
            // console.log('ag:', ag)
            if (opt?.extraProcessor?.[ag.op]) {
              newItem[ag.op] = await opt?.extraProcessor?.[ag.op]?.(ag.value, item)
            } else {
              newItem[ag.op] = ag.value
            }
          }
          returnArray.push(newItem)
        }
      }
      return returnArray
    },
    async getAndroidVersionMetrics({
      durationDays = 1,
      limit = 200,
    }: {
      durationDays?: number
      limit?: number
    } = {}): Promise<AndroidVersionMetrics[]> {
      return this.getVersionMetrics({ platform: 'android', appPrefix: 'APP-Android-', durationDays, limit })
    },
    // RouterPanda: heartbeats with platform 'openwrt' (OpenWrt, Merlin and
    // koolcenter alike), events from x-panda-app APP-openwrt-<version>.
    async getRouterVersionMetrics({
      durationDays = 1,
      limit = 200,
    }: {
      durationDays?: number
      limit?: number
    } = {}): Promise<VersionMetrics[]> {
      return this.getVersionMetrics({ platform: 'openwrt', appPrefix: 'APP-openwrt-', durationDays, limit })
    },
    async getVersionMetrics({
      platform,
      appPrefix,
      durationDays,
      limit,
    }: {
      platform: string
      appPrefix: string
      durationDays: number
      limit: number
    }): Promise<VersionMetrics[]> {
      const safeDuration = Math.max(1, Math.min(Number(durationDays) || 1, 365))
      const safeLimit = Math.max(1, Math.min(Number(limit) || 200, 1000))
      const client = await this.getClient()
      const startTime = toAxiomISOString(dayjs().subtract(safeDuration, 'day'))
      const endTime = toAxiomISOString(dayjs())
      const { heartbeatApl, eventApl } = versionMetricsApl({ platform, appPrefix, limit: safeLimit })

      const [heartbeatRows, eventRows] = await Promise.all([
        client
          .query(heartbeatApl, { startTime, endTime })
          .then((result) => this.toTable(result)),
        client
          .query(eventApl, { startTime, endTime })
          .then((result) => this.toTable(result)),
      ])

      return mergeVersionMetrics(heartbeatRows, eventRows, toEpochMs)
    },
    async getAppleVersionMetrics({
      durationDays = 1,
      limit = 200,
    }: {
      durationDays?: number
      limit?: number
    } = {}): Promise<AppleVersionMetrics[]> {
      const safeDuration = Math.max(1, Math.min(Number(durationDays) || 1, 365))
      const safeLimit = Math.max(1, Math.min(Number(limit) || 200, 1000))
      const client = await this.getClient()
      const startTime = toAxiomISOString(dayjs().subtract(safeDuration, 'day'))
      const endTime = toAxiomISOString(dayjs())

      const heartbeatApl = `['desktopclientheartbeat']
        | extend platform = tostring(platform)
        | where platform == 'ios' or platform == 'ipados' or platform == 'tvos' or platform == 'macos' or platform == 'visionos' or platform == 'apple'
        | where isnotempty(version) and isnotempty(device_uuid)
        | extend version = tostring(version)
        | summarize install_count = dcount(device_uuid), active_users = dcount(userId), heartbeat_count = count(), last_heartbeat = max(_time) by version, platform
        | order by install_count desc
        | limit ${safeLimit}`

      const eventApl = `['client_events']
        | where _clientVersion contains 'APP-iOS-' or _clientVersion contains 'APP-iPadOS-' or _clientVersion contains 'APP-tvOS-' or _clientVersion contains 'APP-macOS-' or _clientVersion contains 'APP-visionOS-' or _clientVersion contains 'APP-Apple-'
        | extend raw_version = tostring(_clientVersion)
        | extend platform = iff(raw_version contains 'APP-iOS-', 'ios', iff(raw_version contains 'APP-iPadOS-', 'ipados', iff(raw_version contains 'APP-tvOS-', 'tvos', iff(raw_version contains 'APP-macOS-', 'macos', iff(raw_version contains 'APP-visionOS-', 'visionos', 'apple')))))
        | extend version = replace_regex(@'^APP-(iOS|iPadOS|tvOS|macOS|visionOS|Apple)-', '', raw_version)
        | where isnotempty(version)
        | extend usage_up = todouble(['metadata.usage_up_delta']), usage_down = todouble(['metadata.usage_down_delta'])
        | summarize launch_count = sum(iff(name == 'Service Started', 1, 0)), usage_event_count = sum(iff(name == 'Service Data Usage', 1, 0)), usage_up_bytes = sum(usage_up), usage_down_bytes = sum(usage_down), event_device_count = dcount(_deviceUuid), event_user_count = dcount(_userId), last_event = max(_time) by version, platform
        | extend traffic_bytes = usage_up_bytes + usage_down_bytes
        | order by traffic_bytes desc
        | limit ${safeLimit}`

      const [heartbeatRows, eventRows] = await Promise.all([
        client
          .query(heartbeatApl, { startTime, endTime })
          .then((result) => this.toTable(result)),
        client
          .query(eventApl, { startTime, endTime })
          .then((result) => this.toTable(result)),
      ])

      const rowsByVersionPlatform = new Map<string, AppleVersionMetrics>()
      const applePlatforms = new Set<AppleMetricPlatformName>(['ios', 'ipados', 'tvos', 'macos', 'visionos', 'apple'])
      const rowFor = (version: any, platform: any) => {
        const versionKey = String(version || '').trim()
        const platformKey = String(platform || '').trim() as AppleMetricPlatformName
        if (!versionKey || !applePlatforms.has(platformKey)) return null
        const key = `${versionKey}::${platformKey}`
        const existing = rowsByVersionPlatform.get(key)
        if (existing) return existing
        const created: AppleVersionMetrics = {
          version: versionKey,
          platform: platformKey,
          installCount: 0,
          activeUsers: 0,
          heartbeatCount: 0,
          launchCount: 0,
          usageEventCount: 0,
          trafficBytes: 0,
          trafficUpBytes: 0,
          trafficDownBytes: 0,
          eventDeviceCount: 0,
          eventUserCount: 0,
          lastHeartbeatAt: 0,
          lastEventAt: 0,
          lastSeenAt: 0,
        }
        rowsByVersionPlatform.set(key, created)
        return created
      }

      for (const row of heartbeatRows || []) {
        const target = rowFor(row.version, row.platform)
        if (!target) continue
        target.installCount = Number(row.install_count || 0)
        target.activeUsers = Number(row.active_users || 0)
        target.heartbeatCount = Number(row.heartbeat_count || 0)
        target.lastHeartbeatAt = toEpochMs(row.last_heartbeat)
      }

      for (const row of eventRows || []) {
        const target = rowFor(row.version, row.platform)
        if (!target) continue
        target.launchCount = Number(row.launch_count || 0)
        target.usageEventCount = Number(row.usage_event_count || 0)
        target.trafficUpBytes = Number(row.usage_up_bytes || 0)
        target.trafficDownBytes = Number(row.usage_down_bytes || 0)
        target.trafficBytes = Number(row.traffic_bytes || target.trafficUpBytes + target.trafficDownBytes)
        target.eventDeviceCount = Number(row.event_device_count || 0)
        target.eventUserCount = Number(row.event_user_count || 0)
        target.lastEventAt = toEpochMs(row.last_event)
      }

      return [...rowsByVersionPlatform.values()]
        .map((row) => ({
          ...row,
          lastSeenAt: Math.max(row.lastHeartbeatAt, row.lastEventAt),
        }))
        .sort((a, b) =>
          b.installCount - a.installCount
          || b.trafficBytes - a.trafficBytes
          || a.version.localeCompare(b.version),
        )
    },
    async getDesktopVersionMetrics({
      durationDays = 1,
      startTime,
      endTime,
      limit = 200,
    }: {
      durationDays?: number
      startTime?: DateInput
      endTime?: DateInput
      limit?: number
    } = {}): Promise<DesktopVersionMetrics[]> {
      const safeDuration = Math.max(1, Math.min(Number(durationDays) || 1, 365))
      const safeLimit = Math.max(1, Math.min(Number(limit) || 200, 1000))
      const client = await this.getClient()
      const hasCustomRange = hasDateInput(startTime) || hasDateInput(endTime)
      const queryRange = hasCustomRange
        ? requireAxiomTimeRange(startTime, endTime)
        : {
            startTime: toAxiomISOString(dayjs().subtract(safeDuration, 'day')),
            endTime: toAxiomISOString(dayjs()),
          }

      const heartbeatApl = `['desktopclientheartbeat']
        | where platform == 'darwin' or platform == 'win32'
        | where isnotempty(version) and isnotempty(device_uuid)
        | extend version = tostring(version), platform = tostring(platform)
        | summarize install_count = dcount(device_uuid), active_users = dcount(userId), heartbeat_count = count(), last_heartbeat = max(_time) by version, platform
        | order by install_count desc
        | limit ${safeLimit}`

      const eventApl = `['client_events']
        | where isnotempty(_clientVersion)
        | extend raw_version = tostring(_clientVersion), os = tostring(_os)
        | extend platform = iff(os contains '-darwin-', 'darwin', iff(os contains '-win32-', 'win32', iff(raw_version contains 'APP-darwin-', 'darwin', iff(raw_version contains 'APP-win32-', 'win32', ''))))
        | where platform == 'darwin' or platform == 'win32'
        | extend version = replace_regex(@'^APP-(darwin|win32)-', '', raw_version)
        | where isnotempty(version)
        | extend usage_up = todouble(['metadata.usage_up_delta']), usage_down = todouble(['metadata.usage_down_delta'])
        | summarize launch_count = sum(iff(name == 'Service Started', 1, 0)), usage_event_count = sum(iff(name == 'Service Data Usage', 1, 0)), usage_up_bytes = sum(usage_up), usage_down_bytes = sum(usage_down), event_device_count = dcount(_deviceUuid), event_user_count = dcount(_userId), last_event = max(_time) by version, platform
        | extend traffic_bytes = usage_up_bytes + usage_down_bytes
        | order by traffic_bytes desc
        | limit ${safeLimit}`

      const [heartbeatRows, eventRows] = await Promise.all([
        client
          .query(heartbeatApl, queryRange)
          .then((result) => this.toTable(result)),
        client
          .query(eventApl, queryRange)
          .then((result) => this.toTable(result)),
      ])

      const rowsByVersionPlatform = new Map<string, DesktopVersionMetrics>()
      const rowFor = (version: any, platform: any) => {
        const versionKey = String(version || '').trim()
        const platformKey = String(platform || '').trim()
        if (!versionKey || (platformKey !== 'darwin' && platformKey !== 'win32')) return null
        const key = `${versionKey}::${platformKey}`
        const existing = rowsByVersionPlatform.get(key)
        if (existing) return existing
        const created: DesktopVersionMetrics = {
          version: versionKey,
          platform: platformKey,
          installCount: 0,
          activeUsers: 0,
          heartbeatCount: 0,
          launchCount: 0,
          usageEventCount: 0,
          trafficBytes: 0,
          trafficUpBytes: 0,
          trafficDownBytes: 0,
          eventDeviceCount: 0,
          eventUserCount: 0,
          lastHeartbeatAt: 0,
          lastEventAt: 0,
          lastSeenAt: 0,
        }
        rowsByVersionPlatform.set(key, created)
        return created
      }

      for (const row of heartbeatRows || []) {
        const target = rowFor(row.version, row.platform)
        if (!target) continue
        target.installCount = Number(row.install_count || 0)
        target.activeUsers = Number(row.active_users || 0)
        target.heartbeatCount = Number(row.heartbeat_count || 0)
        target.lastHeartbeatAt = toEpochMs(row.last_heartbeat)
      }

      for (const row of eventRows || []) {
        const target = rowFor(row.version, row.platform)
        if (!target) continue
        target.launchCount = Number(row.launch_count || 0)
        target.usageEventCount = Number(row.usage_event_count || 0)
        target.trafficUpBytes = Number(row.usage_up_bytes || 0)
        target.trafficDownBytes = Number(row.usage_down_bytes || 0)
        target.trafficBytes = Number(row.traffic_bytes || target.trafficUpBytes + target.trafficDownBytes)
        target.eventDeviceCount = Number(row.event_device_count || 0)
        target.eventUserCount = Number(row.event_user_count || 0)
        target.lastEventAt = toEpochMs(row.last_event)
      }

      return [...rowsByVersionPlatform.values()]
        .map((row) => ({
          ...row,
          lastSeenAt: Math.max(row.lastHeartbeatAt, row.lastEventAt),
        }))
        .sort((a, b) =>
          b.installCount - a.installCount
          || b.trafficBytes - a.trafficBytes
          || a.version.localeCompare(b.version),
        )
    },
    // Per-user breakdown of one desktop version row. `platform` accepts 'all'
    // to match the merged-platform view of the metrics table.
    async getDesktopVersionUsers({
      version,
      platform = 'all',
      startTime,
      endTime,
      durationDays = 1,
      limit = 200,
    }: {
      version: string
      platform?: 'darwin' | 'win32' | 'all'
      startTime?: DateInput
      endTime?: DateInput
      durationDays?: number
      limit?: number
    }): Promise<DesktopVersionUserMetrics[]> {
      const versionKey = String(version || '').trim()
      if (!versionKey) throw new Error('version is required')
      const safeDuration = Math.max(1, Math.min(Number(durationDays) || 1, 365))
      const safeLimit = Math.max(1, Math.min(Number(limit) || 200, 1000))
      const client = await this.getClient()
      const hasCustomRange = hasDateInput(startTime) || hasDateInput(endTime)
      const queryRange = hasCustomRange
        ? requireAxiomTimeRange(startTime, endTime)
        : {
            startTime: toAxiomISOString(dayjs().subtract(safeDuration, 'day')),
            endTime: toAxiomISOString(dayjs()),
          }

      const platformFilter = platform === 'all'
        ? `| where platform == 'darwin' or platform == 'win32'`
        : `| where platform == ${toAplString(platform)}`

      const heartbeatApl = `['desktopclientheartbeat']
        | where isnotempty(version) and isnotempty(device_uuid) and isnotempty(userId)
        | extend version = tostring(version), platform = tostring(platform)
        ${platformFilter}
        | where version == ${toAplString(versionKey)}
        | summarize device_count = dcount(device_uuid), heartbeat_count = count(), os_versions = make_set(tostring(release), 5), last_heartbeat = max(_time) by user_id = tostring(userId)
        | order by heartbeat_count desc
        | limit ${safeLimit}`

      const eventApl = `['client_events']
        | where isnotempty(_clientVersion) and isnotempty(_userId)
        | extend raw_version = tostring(_clientVersion), os = tostring(_os)
        | extend platform = iff(os contains '-darwin-', 'darwin', iff(os contains '-win32-', 'win32', iff(raw_version contains 'APP-darwin-', 'darwin', iff(raw_version contains 'APP-win32-', 'win32', ''))))
        ${platformFilter}
        | extend version = replace_regex(@'^APP-(darwin|win32)-', '', raw_version)
        | where version == ${toAplString(versionKey)}
        | extend usage_up = todouble(['metadata.usage_up_delta']), usage_down = todouble(['metadata.usage_down_delta'])
        | extend os_version = replace_regex(@'^.*-(darwin|win32)-', '', os)
        | summarize launch_count = sum(iff(name == 'Service Started', 1, 0)), usage_event_count = sum(iff(name == 'Service Data Usage', 1, 0)), usage_up_bytes = sum(usage_up), usage_down_bytes = sum(usage_down), device_count = dcount(_deviceUuid), os_versions = make_set(os_version, 5), last_event = max(_time) by user_id = tostring(_userId)
        | order by usage_up_bytes desc
        | limit ${safeLimit}`

      const [heartbeatRows, eventRows] = await Promise.all([
        client.query(heartbeatApl, queryRange).then((result) => this.toTable(result)),
        client.query(eventApl, queryRange).then((result) => this.toTable(result)),
      ])

      const rowsByUser = new Map<string, DesktopVersionUserMetrics>()
      const rowFor = (userId: any) => {
        const key = String(userId || '').trim()
        if (!key || key === '0') return null
        const existing = rowsByUser.get(key)
        if (existing) return existing
        const created: DesktopVersionUserMetrics = {
          userId: key,
          deviceCount: 0,
          osVersions: [],
          heartbeatCount: 0,
          launchCount: 0,
          usageEventCount: 0,
          trafficBytes: 0,
          trafficUpBytes: 0,
          trafficDownBytes: 0,
          lastHeartbeatAt: 0,
          lastEventAt: 0,
          lastSeenAt: 0,
        }
        rowsByUser.set(key, created)
        return created
      }

      for (const row of heartbeatRows || []) {
        const target = rowFor(row.user_id)
        if (!target) continue
        target.deviceCount = Number(row.device_count || 0)
        target.heartbeatCount = Number(row.heartbeat_count || 0)
        target.osVersions = toStringArray(row.os_versions)
        target.lastHeartbeatAt = toEpochMs(row.last_heartbeat)
      }

      for (const row of eventRows || []) {
        const target = rowFor(row.user_id)
        if (!target) continue
        target.launchCount = Number(row.launch_count || 0)
        target.usageEventCount = Number(row.usage_event_count || 0)
        target.trafficUpBytes = Number(row.usage_up_bytes || 0)
        target.trafficDownBytes = Number(row.usage_down_bytes || 0)
        target.trafficBytes = target.trafficUpBytes + target.trafficDownBytes
        // Heartbeats are the authoritative device count; events only fill in
        // for users that never reached the heartbeat dataset in this window.
        if (!target.deviceCount) target.deviceCount = Number(row.device_count || 0)
        if (!target.osVersions.length) target.osVersions = toStringArray(row.os_versions)
        target.lastEventAt = toEpochMs(row.last_event)
      }

      return [...rowsByUser.values()]
        .map((row) => ({
          ...row,
          lastSeenAt: Math.max(row.lastHeartbeatAt, row.lastEventAt),
        }))
        .sort((a, b) =>
          b.trafficBytes - a.trafficBytes
          || b.heartbeatCount - a.heartbeatCount
          || a.userId.localeCompare(b.userId),
        )
    },
    async getServerUsageDashboard({
      startTime,
      endTime,
      limit = 100,
      userLimit = 50,
    }: {
      startTime?: DateInput
      endTime?: DateInput
      limit?: number
      userLimit?: number
    } = {}): Promise<AxiomServerUsageDashboard> {
      const safeLimit = Math.max(1, Math.min(Number(limit) || 100, 1000))
      const safeUserLimit = Math.max(1, Math.min(Number(userLimit) || 50, 500))
      const queryStartTime = toAxiomISOString(startTime, dayjs().subtract(1, 'day'))
      const queryEndTime = toAxiomISOString(endTime)
      const client = await this.getClient()
      const baseApl = serverUsageBaseApl()

      const summaryApl = `${baseApl}
        | summarize total_bytes = sum(bytes), user_count = dcount(userId), ip_count = dcount(userIp), record_count = count(), server_count = dcount(serverName), protocol_count = dcount(protocolName), first_seen = min(_time), last_seen = max(_time)`
      const serverApl = `${baseApl}
        | summarize total_bytes = sum(bytes), user_count = dcount(userId), ip_count = dcount(userIp), record_count = count(), protocol_count = dcount(protocolName), protocols = make_set(protocolName, 30), first_seen = min(_time), last_seen = max(_time) by server = serverName
        | order by total_bytes desc
        | limit ${safeLimit}`
      const protocolApl = `${baseApl}
        | summarize total_bytes = sum(bytes), user_count = dcount(userId), server_count = dcount(serverName), record_count = count(), first_seen = min(_time), last_seen = max(_time) by protocol = protocolName
        | order by total_bytes desc
        | limit ${safeLimit}`
      const serverProtocolApl = `${baseApl}
        | summarize total_bytes = sum(bytes), user_count = dcount(userId), ip_count = dcount(userIp), record_count = count(), first_seen = min(_time), last_seen = max(_time) by server = serverName, protocol = protocolName, protocol_detail = protocolDetail
        | order by total_bytes desc
        | limit ${safeLimit}`
      const userApl = `${baseApl}
        | summarize total_bytes = sum(bytes), server_count = dcount(serverName), protocol_count = dcount(protocolName), ip_count = dcount(userIp), record_count = count(), servers = make_set(serverName, 30), protocols = make_set(protocolName, 30), first_seen = min(_time), last_seen = max(_time) by userId
        | order by total_bytes desc
        | limit ${safeUserLimit}`

      const [summaryRows, serverRows, protocolRows, serverProtocolRows, userRows] = await Promise.all([
        client.query(summaryApl, { startTime: queryStartTime, endTime: queryEndTime }).then(result => this.toTable(result)),
        client.query(serverApl, { startTime: queryStartTime, endTime: queryEndTime }).then(result => this.toTable(result)),
        client.query(protocolApl, { startTime: queryStartTime, endTime: queryEndTime }).then(result => this.toTable(result)),
        client.query(serverProtocolApl, { startTime: queryStartTime, endTime: queryEndTime }).then(result => this.toTable(result)),
        client.query(userApl, { startTime: queryStartTime, endTime: queryEndTime }).then(result => this.toTable(result)),
      ])

      const summaryRow = summaryRows?.[0] || {}
      const summary: AxiomServerUsageSummary = {
        totalBytes: Number(summaryRow.total_bytes || 0),
        userCount: Number(summaryRow.user_count || 0),
        ipCount: Number(summaryRow.ip_count || 0),
        recordCount: Number(summaryRow.record_count || 0),
        serverCount: Number(summaryRow.server_count || 0),
        protocolCount: Number(summaryRow.protocol_count || 0),
        firstSeenAt: toEpochMs(summaryRow.first_seen),
        lastSeenAt: toEpochMs(summaryRow.last_seen),
      }

      return {
        summary,
        servers: (serverRows || []).map((row: any) => ({
          server: String(row.server || ''),
          totalBytes: Number(row.total_bytes || 0),
          userCount: Number(row.user_count || 0),
          ipCount: Number(row.ip_count || 0),
          recordCount: Number(row.record_count || 0),
          protocolCount: Number(row.protocol_count || 0),
          protocols: toStringArray(row.protocols),
          firstSeenAt: toEpochMs(row.first_seen),
          lastSeenAt: toEpochMs(row.last_seen),
        })),
        protocols: (protocolRows || []).map((row: any) => ({
          protocol: String(row.protocol || ''),
          protocolDetail: String(row.protocol_detail || row.protocol || ''),
          totalBytes: Number(row.total_bytes || 0),
          userCount: Number(row.user_count || 0),
          serverCount: Number(row.server_count || 0),
          recordCount: Number(row.record_count || 0),
          firstSeenAt: toEpochMs(row.first_seen),
          lastSeenAt: toEpochMs(row.last_seen),
        })),
        serverProtocols: (serverProtocolRows || []).map((row: any) => ({
          server: String(row.server || ''),
          protocol: String(row.protocol || ''),
          protocolDetail: String(row.protocol_detail || row.protocol || ''),
          totalBytes: Number(row.total_bytes || 0),
          userCount: Number(row.user_count || 0),
          ipCount: Number(row.ip_count || 0),
          recordCount: Number(row.record_count || 0),
          firstSeenAt: toEpochMs(row.first_seen),
          lastSeenAt: toEpochMs(row.last_seen),
        })),
        users: (userRows || []).map((row: any) => ({
          userId: String(row.userId || ''),
          totalBytes: Number(row.total_bytes || 0),
          serverCount: Number(row.server_count || 0),
          protocolCount: Number(row.protocol_count || 0),
          ipCount: Number(row.ip_count || 0),
          recordCount: Number(row.record_count || 0),
          servers: toStringArray(row.servers),
          protocols: toStringArray(row.protocols),
          firstSeenAt: toEpochMs(row.first_seen),
          lastSeenAt: toEpochMs(row.last_seen),
        })),
        startTime: queryStartTime,
        endTime: queryEndTime,
      }
    },
    async getServerUsageDetails({
      server,
      startTime,
      endTime,
      userLimit = 50,
      protocolLimit = 100,
    }: {
      server: string
      startTime?: DateInput
      endTime?: DateInput
      userLimit?: number
      protocolLimit?: number
    }): Promise<AxiomServerUsageServerDetails> {
      const serverName = String(server || '').trim()
      if (!serverName) throw new Error('server is required')
      const safeUserLimit = Math.max(1, Math.min(Number(userLimit) || 50, 500))
      const safeProtocolLimit = Math.max(1, Math.min(Number(protocolLimit) || 100, 500))
      const queryStartTime = toAxiomISOString(startTime, dayjs().subtract(1, 'day'))
      const queryEndTime = toAxiomISOString(endTime)
      const client = await this.getClient()
      const baseApl = serverUsageBaseApl(serverName)
      const protocolApl = `${baseApl}
        | summarize total_bytes = sum(bytes), user_count = dcount(userId), ip_count = dcount(userIp), record_count = count(), first_seen = min(_time), last_seen = max(_time) by server = serverName, protocol = protocolName, protocol_detail = protocolDetail
        | order by total_bytes desc
        | limit ${safeProtocolLimit}`
      const userApl = `${baseApl}
        | summarize total_bytes = sum(bytes), protocol_count = dcount(protocolName), ip_count = dcount(userIp), record_count = count(), protocols = make_set(protocolName, 30), first_seen = min(_time), last_seen = max(_time) by server = serverName, userId
        | order by total_bytes desc
        | limit ${safeUserLimit}`

      const [protocolRows, userRows] = await Promise.all([
        client.query(protocolApl, { startTime: queryStartTime, endTime: queryEndTime }).then(result => this.toTable(result)),
        client.query(userApl, { startTime: queryStartTime, endTime: queryEndTime }).then(result => this.toTable(result)),
      ])

      return {
        server: serverName,
        protocols: (protocolRows || []).map((row: any) => ({
          server: String(row.server || serverName),
          protocol: String(row.protocol || ''),
          protocolDetail: String(row.protocol_detail || row.protocol || ''),
          totalBytes: Number(row.total_bytes || 0),
          userCount: Number(row.user_count || 0),
          ipCount: Number(row.ip_count || 0),
          recordCount: Number(row.record_count || 0),
          firstSeenAt: toEpochMs(row.first_seen),
          lastSeenAt: toEpochMs(row.last_seen),
        })),
        users: (userRows || []).map((row: any) => ({
          server: String(row.server || serverName),
          userId: String(row.userId || ''),
          totalBytes: Number(row.total_bytes || 0),
          protocolCount: Number(row.protocol_count || 0),
          ipCount: Number(row.ip_count || 0),
          recordCount: Number(row.record_count || 0),
          protocols: toStringArray(row.protocols),
          firstSeenAt: toEpochMs(row.first_seen),
          lastSeenAt: toEpochMs(row.last_seen),
        })),
        startTime: queryStartTime,
        endTime: queryEndTime,
      }
    },
    async getCustomEgressTimeline({
      userId,
      serviceId,
      startTime,
      endTime,
      limit = 200,
    }: {
      userId: number
      serviceId: number
      startTime?: DateInput
      endTime?: DateInput
      limit?: number
    }): Promise<CustomEgressAuditTimelineEntry[]> {
      if (!userId || !serviceId) return []
      const safeLimit = Math.max(1, Math.min(Number(limit) || 200, 500))
      const client = await this.getClient()
      const apl = `['custom_egress_operations']
        | where tolong(['target_user_id']) == ${Number(userId)}
        | where tolong(['service_id']) == ${Number(serviceId)}
        | order by _time desc
        | limit ${safeLimit}`
      const result = await client.query(apl, {
        startTime: toAxiomISOString(startTime, dayjs().subtract(90, 'day')),
        endTime: toAxiomISOString(endTime),
      })
      return (result?.matches || []).map((match: any) => {
        const data = match?.data || {}
        return {
          time: data._time || match?._time || data.timestamp || null,
          ...data,
        }
      })
    },
    async getDesktopClientSummary(_userId: number | number[], startTime: string, endTime: string): Promise<any> {
      let userId = _userId
      if (typeof _userId === 'number') {
        userId = [_userId]
      }
      const client = await this.getClient()
      const apl = `desktopclientheartbeat
        | where userId in (${(userId as number[]).join(',')})
        | extend userName = iff(userData contains "\\\\", split(userData, '\\\\')[2], split(userData, '/')[2])
        | extend uuidHardwareUserNameFirstMac = strcat(['uuid.hardware'], userName, ['uuid.macs'][0])
        | summarize uniqDeviceCount = dcount(uuidHardwareUserNameFirstMac), ipSet = make_set(ip), userNameSet = make_set(userName) by userId`
      return await client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
    },
    async getIPConnectionSummary(_userId: number | number[], startTime: string, endTime: string): Promise<any> {
      let userId = _userId
      if (typeof _userId === 'number') {
        userId = [_userId]
      }
      const client = await this.getClient()
      const apl = `['pandafanproxyservers']
        | where ['user-id'] in (${(userId as number[]).join(',')}) and isnotempty(['user-address'])
        | extend serverWithProtocol = strcat(split(['server'], '-')[0], ':', ['protocol'])
        | extend MB = tolong(['size-in-byte']) / 1024 / 1024
        | extend pureIp = iff(['user-address'] contains ":", split(['user-address'], ':')[0], ['user-address'])
        | summarize set_server=make_set(serverWithProtocol), min(_time), max(_time), usedMB = sum(MB) by pureIp
        | limit 1000`
      return await client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
    },
    async getStatLastPostTime({ limit, startTime, endTime }: { limit?: number; startTime?: string; endTime?: string }) {
      const client = await this.getClient()
      const apl = `['pandafanproxyservers']
        | summarize max(_time) by server, protocol
        | limit ${limit || 120}
        | order by server asc`
      return await client.query(apl, {
        startTime: toAxiomISOString(startTime, dayjs().subtract(2, 'minute')),
        endTime: toAxiomISOString(endTime),
      })
    },
    async getAutostatTopUsers({
      startTime,
      endTime,
      limit = 100,
    }: {
      startTime: string | Dayjs
      endTime: string | Dayjs
      limit?: number
    }) {
      const client = await this.getClient()
      const safeLimit = Math.min(Math.max(1, Number(limit) || 100), 500)
      const apl = `['autostatdata']
        | extend numericUserId = tolong(userId), numericTr = tolong(tr)
        | where numericUserId > 0 and isnotnull(numericTr)
        | summarize totalTr = sum(numericTr), serverCount = dcount(server), protocolCount = dcount(protocol), servers = make_set(server, 50), protocols = make_set(protocol, 20), lastSeen = max(_time) by userId = numericUserId
        | order by totalTr desc, userId asc
        | limit ${safeLimit}`
      return await client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
    },
    async getAutostatUserUsageBreakdown({
      userId,
      startTime,
      endTime,
      limit = 50,
    }: {
      userId: number
      startTime: string | Dayjs
      endTime: string | Dayjs
      limit?: number
    }) {
      const client = await this.getClient()
      const safeUserId = Math.max(1, Number(userId) || 0)
      const safeLimit = Math.min(Math.max(1, Number(limit) || 50), 200)
      const apl = `['autostatdata']
        | extend numericUserId = tolong(userId), numericTr = tolong(tr)
        | where numericUserId == ${safeUserId} and isnotnull(numericTr)
        | summarize totalTr = sum(numericTr) by server, protocol
        | order by totalTr desc, server asc, protocol asc
        | limit ${safeLimit}`
      return await client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
    },
    async getServerRealTimeSpeedHistory({
      server,
      servers,
      startTime,
      endTime,
      binMinutes = 5,
      aggregate = 'avg',
      allServers = false,
    }: {
      server?: string
      servers?: string[]
      startTime: string | Dayjs
      endTime: string | Dayjs
      binMinutes?: number
      aggregate?: 'avg' | 'sum'
      allServers?: boolean
    }) {
      const serverList = (servers || []).filter(Boolean)
      if (server) {
        serverList.push(server)
      }
      if (!serverList.length && !allServers) {
        return null
      }
      const client = await this.getClient()
      const safeBinMinutes = Math.max(1, Math.min(120, Math.round(Number(binMinutes) || 5)))
      const serverFilter = realTimeSpeedServerFilter(serverList, allServers)
      const baseQuery = `['serverrealtimespeedreport']
        ${serverFilter}
        | extend rx = ['speed.in'], tx = ['speed.out']
        | where isnotnull(rx) or isnotnull(tx)`
      const summarize =
        serverList.length > 1 || allServers
          ? aggregate === 'sum'
            ? `| summarize rx = avg(rx), tx = avg(tx) by bin_time = bin(_time, ${safeBinMinutes}m), server
        | summarize rx = sum(rx), tx = sum(tx) by bin_time`
            : `| summarize rx = avg(rx), tx = avg(tx) by bin_time = bin(_time, ${safeBinMinutes}m), server
        | summarize rx = avg(rx), tx = avg(tx) by bin_time`
          : `| summarize rx = avg(rx), tx = avg(tx) by bin_time = bin(_time, ${safeBinMinutes}m)`
      const apl = `${baseQuery}
        ${summarize}
        | order by bin_time asc`
      return await client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
    },
    async getServerRealTimeSpeedUsageSummary({
      server,
      servers,
      startTime,
      endTime,
      binMinutes = 5,
      allServers = false,
    }: {
      server?: string
      servers?: string[]
      startTime: string | Dayjs
      endTime: string | Dayjs
      binMinutes?: number
      allServers?: boolean
    }) {
      const serverList = (servers || []).filter(Boolean)
      if (server) {
        serverList.push(server)
      }
      if (!serverList.length && !allServers) {
        return null
      }
      const client = await this.getClient()
      const safeBinMinutes = Math.max(1, Math.min(120, Math.round(Number(binMinutes) || 5)))
      const serverFilter = realTimeSpeedServerFilter(serverList, allServers)

      const apl = `['serverrealtimespeedreport']
        ${serverFilter}
        | extend total_in = tolong(['speed.total_in']), total_out = tolong(['speed.total_out'])
        | where isnotnull(total_in) or isnotnull(total_out)
        | summarize start_in = min(total_in), end_in = max(total_in), start_out = min(total_out), end_out = max(total_out) by server
        | extend usage_in = end_in - start_in, usage_out = end_out - start_out
        | summarize usage_in = sum(usage_in), usage_out = sum(usage_out)`

      return await client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
    },
    async getUsageHistoryGroupByIP(userId: number, startTime: string, endTime: string): Promise<any> {
      const cacheKey = `getUsageHistoryGroupByIP-${userId}-${startTime}-${endTime}`
      if (this.resultCache.has(cacheKey)) {
        console.log('returning from cache.', cacheKey)
        return this.resultCache.get(cacheKey)
      }
      const apl = `pandafanproxyservers
        | where ['user-id'] == ${userId}
        | extend userIp1 = extract("(.*):.*", 1, ['user-address'])
        | extend userIp = iff(isempty(userIp1), ['user-address'], userIp1)
        | summarize totalByte = sum(tolong(['size-in-byte'])), totalAccess = count() by bin_auto(_time), userIp
        | limit 10000`
      const client = await this.getClient()
      const resultPromise = client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
      this.resultCache.set(cacheKey, resultPromise)
      return resultPromise
    },
    async getUserConfigfileVisitRecords(
      userId: number,
      startTime: string | Dayjs,
      endTime: string | Dayjs,
    ): Promise<any> {
      const cacheKey = `getUserConfigfileVisitRecords-${userId}-${startTime}-${endTime}`
      if (this.resultCache.has(cacheKey)) {
        console.log('returning from cache.', cacheKey)
        return this.resultCache.get(cacheKey)
      }
      const apl = `['configfilevisit']
        | where userId == ${userId}
        | order by _time
        | summarize
            lastVisit = max(_time) / 1000 / 1000,
            firstVisit = min(_time) / 1000 / 1000,
            visitCount = count()
          by ua, configType, oss_request_id = ['headers.oss-request-id']
        | limit 1000`
      const client = await this.getClient()
      const resultPromise = client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
      this.resultCache.set(cacheKey, resultPromise)
      return resultPromise
    },
    // 每个 UA 每小时的请求次数;hour 为自 1970-01-01 起的小时序号(用整数分组才能出现在 totals 里)
    async getUserConfigfileVisitHourly(
      userId: number,
      startTime: string | Dayjs,
      endTime: string | Dayjs,
    ): Promise<any> {
      const cacheKey = `getUserConfigfileVisitHourly-${userId}-${startTime}-${endTime}`
      if (this.resultCache.has(cacheKey)) {
        return this.resultCache.get(cacheKey)
      }
      const apl = `['configfilevisit']
        | where userId == ${userId}
        | summarize visitCount = count() by ua, hour = datetime_diff('hour', _time, datetime(1970-01-01))
        | limit 20000`
      const client = await this.getClient()
      const resultPromise = client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
      this.resultCache.set(cacheKey, resultPromise)
      return resultPromise
    },
    async getClientDeviceAbusers({
      durationDays,
      minDeviceCount,
      minIpCount,
      limit = 2000,
    }: {
      durationDays: number
      minDeviceCount: number
      minIpCount: number
      limit?: number
    }): Promise<any> {
      const safeDuration = Math.max(1, Number(durationDays) || 1)
      const safeDeviceCount = Math.max(1, Number(minDeviceCount) || 1)
      const safeIpCount = Math.max(0, Number(minIpCount) || 0)
      const safeLimit = Math.min(Math.max(1, Number(limit) || 2000), 5000)
      const client = await this.getClient()
      const apl = `['client_events']
        | extend userId = tolong(['_userId'])
        | extend deviceId = tostring(['_deviceUuid'])
        | extend reqIp = tostring(['_reqIp'])
        | extend eventName = tostring(name)
        | where isnotempty(userId)
        | summarize device_count = dcount(deviceId), ip_count = dcount(reqIp), devices = make_set(deviceId, 50), ips = make_set(reqIp, 50), record_count = count(), last_seen = max(_time) / 1000 / 1000, service_start_count = sum(iff(eventName == "Service Started", 1, 0)), service_usage_count = sum(iff(eventName == "Service Data Usage", 1, 0)), server_change_count = sum(iff(eventName == "Server Changed", 1, 0)) by userId
        | where device_count >= ${safeDeviceCount} and ip_count >= ${safeIpCount}
        | order by device_count desc, ip_count desc, last_seen desc
        | limit ${safeLimit}`
      return await client.query(apl, {
        startTime: toAxiomISOString(dayjs().subtract(safeDuration, 'day')),
        endTime: toAxiomISOString(dayjs()),
      })
    },
    async getClientConcurrentUsageAbusers({
      durationDays,
      binMinutes = 10,
      minConcurrentBins = 1,
      usageUpDeltaMB = 5,
      limit = 2000,
    }: {
      durationDays: number
      binMinutes?: number
      minConcurrentBins?: number
      usageUpDeltaMB?: number
      limit?: number
    }): Promise<any> {
      const safeDuration = Math.max(1, Number(durationDays) || 1)
      const safeBin = Math.max(1, Number(binMinutes) || 10)
      const safeMinBins = Math.max(1, Number(minConcurrentBins) || 1)
      const safeUsageBytes = Math.max(0, Number(usageUpDeltaMB) || 0) * 1024 * 1024
      const safeLimit = Math.min(Math.max(1, Number(limit) || 2000), 5000)
      const client = await this.getClient()
      const apl = `['client_events']
        | extend userId = tolong(['_userId'])
        | extend deviceId = tostring(['_deviceUuid'])
        | extend reqIp = tostring(['_reqIp'])
        | extend usageUp = todouble(['metadata.usage_up_delta'])
        | extend isUsage = name == "Service Data Usage" and usageUp > ${safeUsageBytes}
        | extend isStart = name == "Service Started"
        | where isUsage or isStart
        | where isnotempty(userId) and isnotempty(deviceId) and isnotempty(reqIp)
        | summarize devs = dcount(deviceId), ips = dcount(reqIp) by userId, bin(_time, ${safeBin}m)
        | where devs >= 2
        | summarize concurrent_bins = count(), high_conf_bins = countif(ips >= 2), max_concurrent_devs = max(devs), max_concurrent_ips = max(ips), last_concurrent_time = max(_time) / 1000 / 1000 by userId
        | where concurrent_bins >= ${safeMinBins}
        | order by high_conf_bins desc, concurrent_bins desc, max_concurrent_devs desc, max_concurrent_ips desc, last_concurrent_time desc
        | limit ${safeLimit}`
      return await client.query(apl, {
        startTime: toAxiomISOString(dayjs().subtract(safeDuration, 'day')),
        endTime: toAxiomISOString(dayjs()),
      })
    },
    async getPandaEnvoyHealthDashboard({
      durationHours = 6,
      limit = 720,
    }: {
      durationHours?: number
      limit?: number
    } = {}): Promise<PandaEnvoyDashboard> {
      const safeHours = Math.max(1, Math.min(Number(durationHours) || 6, 168))
      const safeLimit = Math.max(50, Math.min(Number(limit) || 720, 5000))
      const startTime = toAxiomISOString(dayjs().subtract(safeHours, 'hour'))
      const endTime = toAxiomISOString(dayjs())
      const client = await this.getClient()
      const base = `['panda_envoy_events']`
      const instanceApl = `${base}
        | where kind == "envoy_instance"
        | order by _time desc
        | limit ${safeLimit}`
      const hostApl = `${base}
        | where kind == "host"
        | order by _time desc
        | limit ${Math.min(safeLimit, 1000)}`

      const [instanceRows, hostRows] = await Promise.all([
        client.query(instanceApl, { startTime, endTime }).then(result => this.toTable(result)),
        client.query(hostApl, { startTime, endTime }).then(result => this.toTable(result)),
      ])

      return {
        instances: instanceRows || [],
        hostEvents: hostRows || [],
        startTime,
        endTime,
      }
    },
    // One page of App Store audit events, newest first. `before` is the
    // previous page's oldest time. Pushes and requests are completed so each
    // reads as one unit: with a narrowing filter, every matched push (by
    // notification_uuid) and every request in the results (by request_id);
    // without one, when more pages follow, every request on the page: the
    // page limit can cut any request still open at the boundary, not only the
    // oldest group's. `incomplete` says a follow-up query hit its limit.
    async getAppStoreAuditEvents({
      filters,
      startTime,
      endTime,
      before,
      limit = 100,
    }: {
      filters: AuditFilters
      startTime: string
      endTime: string
      before?: string | null
      limit?: number
    }): Promise<{ events: AppStoreAuditEvent[], hasMore: boolean, oldest: string | null, incomplete: boolean }> {
      const range = requireAxiomTimeRange(startTime, endTime)
      const client = await this.getClient()
      // +1 ms keeps events sharing the cursor's millisecond; merging drops repeats.
      const pageEnd = before ? dayjs(before).add(1, 'millisecond').toISOString() : range.endTime
      const page = auditEventsFromMatches((await client.query(appStoreAuditApl(filters, limit), {
        startTime: range.startTime,
        endTime: pageEnd,
      })).matches)
      const hasMore = page.length >= limit
      const oldest = page.at(-1)?._time ?? null
      let events = page
      let incomplete = false
      const fetchRequests = async (requestIds: string[]) => {
        if (!requestIds.length) return
        const rows = auditEventsFromMatches((await client.query(appStoreAuditRequestsApl(requestIds), range)).matches)
        incomplete ||= rows.length >= AUDIT_REQUESTS_LIMIT
        events = mergeAuditEvents(events, rows)
      }

      if (!page.length) return { events, hasMore, oldest, incomplete }
      if (!auditFiltersNarrow(filters)) {
        if (hasMore) await fetchRequests(unfetchedRequests(page, new Set()))
        return { events, hasMore, oldest, incomplete }
      }
      const ids = (values: unknown[]) => [...new Set(values.filter(value => value != null && value !== '').map(String))]
      const uuids = ids(page.map(event => event.notification_uuid))
      const requestIds = ids(page.map(event => event.request_id))
      if (uuids.length || requestIds.length) {
        const siblings = auditEventsFromMatches((await client.query(appStoreAuditSiblingsApl(uuids, requestIds), range)).matches)
        incomplete ||= siblings.length >= AUDIT_SIBLINGS_LIMIT
        events = mergeAuditEvents(events, siblings)
      }
      // Deliveries brought in by UUID whose other events (written before
      // verification) carry only their request ID.
      await fetchRequests(unfetchedRequests(events, new Set(requestIds)))
      return { events, hasMore, oldest, incomplete }
    },
    async getClientServiceEvents(userId: number, startTime: string, endTime: string): Promise<any> {
      const cacheKey = `getClientServiceEvents-${userId}-${startTime}-${endTime}`
      if (this.resultCache.has(cacheKey)) {
        console.log('returning from cache.', cacheKey)
        return this.resultCache.get(cacheKey)
      }
      const apl = `['client_events']
        | where tolong(['_userId']) == ${userId}
        | where name in ("Service Started", "Service Stopped", "Service Data Usage", "Server Changed", "Auto Server Selected", "Protocol Changed", "Proxy Mode Changed", "TUN Mode Changed")
        | order by _time desc
        | limit 800`
      const client = await this.getClient()
      const resultPromise = client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
      this.resultCache.set(cacheKey, resultPromise)
      return resultPromise
    },
    async getClientLatestServiceUsageEvent(
      userId: number,
      connectionId: string,
      startTime: string,
      endTime: string,
    ): Promise<any> {
      if (!connectionId) {
        return null
      }
      const client = await this.getClient()
      const connectionLiteral = JSON.stringify(connectionId)
      const apl = `['client_events']
        | where tolong(['_userId']) == ${userId}
        | where name == "Service Data Usage"
        | where ['metadata.connection_id'] == ${connectionLiteral}
        | order by _time desc
        | limit 1`
      return await client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
    },
    async getClientServerChangeEvents(
      userId: number,
      connectionId: string,
      startTime: string,
      endTime: string,
    ): Promise<any> {
      if (!connectionId) {
        return null
      }
      const client = await this.getClient()
      const connectionLiteral = JSON.stringify(connectionId)
      const apl = `['client_events']
        | where tolong(['_userId']) == ${userId}
        | where name in ("Server Changed", "Auto Server Selected")
        | where ['metadata.connection_id'] == ${connectionLiteral}
        | order by _time desc
        | limit 50`
      return await client.query(apl, {
        startTime: toAxiomISOString(startTime),
        endTime: toAxiomISOString(endTime),
      })
    },
  },
})
